import { create } from 'zustand';

interface IStudioStore {
    // Audio generation state
    isAudioGenerating: boolean;
    setIsAudioGenerating: (isGenerating: boolean) => void;
    
    // Playing state for block previews
    playingBlockHash: string | null;
    setPlayingBlockHash: (blockHash: string | null) => void;
    
    // Audio player state (for block previews)
    isPlaying: boolean;
    setIsPlaying: (isPlaying: boolean) => void;
    audio: string | null;
    setAudio: (audio: string | null) => void;
    
    // Composer audio player state (migrated from Redux)
    composerAudio: string | null;
    setComposerAudio: (audio: string | null) => void;
    isComposerPlaying: boolean;
    setIsComposerPlaying: (isPlaying: boolean) => void;
    composerPlayerSeek: number;
    setComposerPlayerSeek: (seek: number) => void;
    composerAudioDuration: number;
    setComposerAudioDuration: (duration: number) => void;
    
    // Modal states (migrated from Redux)
    audioTrackUploadModalShow: boolean;
    setAudioTrackUploadModalShow: (show: boolean) => void;
    audioGenerateModalShow: boolean;
    setAudioGenerateModalShow: (show: boolean) => void;
    deleteBlockModalShow: boolean;
    setDeleteBlockModalShow: (show: boolean) => void;
    showNoCreditsModal: boolean;
    setShowNoCreditsModal: (show: boolean) => void;
    isToggleTitleEditorModalShow: boolean;
    setIsToggleTitleEditorModalShow: (show: boolean) => void;
    
    // Music and Sound Effects modals
    showMusicModal: boolean;
    setShowMusicModal: (show: boolean) => void;
    showSoundEffectsModal: boolean;
    setShowSoundEffectsModal: (show: boolean) => void;
    
    // Preview audio cache (blockHash -> audio URL)
    previewAudioCache: Record<string, string>;
    setPreviewAudioCache: (cache: Record<string, string>) => void;
    addToPreviewCache: (blockHash: string, audioUrl: string) => void;
    
    // Content ID (for drafts)
    contentId: string | null;
    setContentId: (contentId: string | null) => void;

    // True when the loaded content contains voices from deprecated providers (e.g. Azure).
    // Editor components use this to fetch the legacy voice list so existing blocks render correctly.
    needsLegacyVoices: boolean;
    setNeedsLegacyVoices: (needs: boolean) => void;

    emotionNudge: boolean;
    setEmotionNudge: (show: boolean) => void;

    // Reset all state
    reset: () => void;
}

const initialState = {
    isAudioGenerating: false,
    playingBlockHash: null,
    isPlaying: false,
    audio: null,
    composerAudio: null,
    isComposerPlaying: false,
    composerPlayerSeek: 0,
    composerAudioDuration: 0,
    audioTrackUploadModalShow: false,
    audioGenerateModalShow: false,
    deleteBlockModalShow: false,
    showNoCreditsModal: false,
    isToggleTitleEditorModalShow: false,
    showMusicModal: false,
    showSoundEffectsModal: false,
    previewAudioCache: {},
    contentId: null,
    needsLegacyVoices: false,
    emotionNudge: false,
};

export const useStudioStore = create<IStudioStore>()((set) => ({
    ...initialState,
    
    setIsAudioGenerating: (isGenerating: boolean) => {
        set({ isAudioGenerating: isGenerating });
    },
    
    setPlayingBlockHash: (blockHash: string | null) => {
        set({ playingBlockHash: blockHash });
    },
    
    setIsPlaying: (isPlaying: boolean) => {
        set({ isPlaying });
    },
    
    setAudio: (audio: string | null) => {
        set({ audio });
    },
    
    setComposerAudio: (audio: string | null) => {
        set({ composerAudio: audio });
    },
    
    setIsComposerPlaying: (isPlaying: boolean) => {
        set({ isComposerPlaying: isPlaying });
    },
    
    setComposerPlayerSeek: (seek: number) => {
        set({ composerPlayerSeek: seek });
    },
    
    setComposerAudioDuration: (duration: number) => {
        set({ composerAudioDuration: duration });
    },
    
    setAudioTrackUploadModalShow: (show: boolean) => {
        set({ audioTrackUploadModalShow: show });
    },
    
    setAudioGenerateModalShow: (show: boolean) => {
        set({ audioGenerateModalShow: show });
    },
    
    setDeleteBlockModalShow: (show: boolean) => {
        set({ deleteBlockModalShow: show });
    },
    
    setShowNoCreditsModal: (show: boolean) => {
        set({ showNoCreditsModal: show });
    },
    
    setIsToggleTitleEditorModalShow: (show: boolean) => {
        set({ isToggleTitleEditorModalShow: show });
    },
    
    setShowMusicModal: (show: boolean) => {
        set({ showMusicModal: show });
    },
    
    setShowSoundEffectsModal: (show: boolean) => {
        set({ showSoundEffectsModal: show });
    },
    
    setPreviewAudioCache: (cache: Record<string, string>) => {
        set({ previewAudioCache: cache });
    },
    
    addToPreviewCache: (blockHash: string, audioUrl: string) => {
        set((state) => ({
            previewAudioCache: {
                ...state.previewAudioCache,
                [blockHash]: audioUrl,
            },
        }));
    },
    
    setContentId: (contentId: string | null) => {
        set({ contentId });
    },

    setNeedsLegacyVoices: (needs: boolean) => {
        set({ needsLegacyVoices: needs });
    },

    setEmotionNudge: (show: boolean) => {
        set({ emotionNudge: show });
    },

    reset: () => {
        set(initialState);
    },
}));

